import { TUNING } from '~/config/tuning'
// Explicit (not auto-) imports so the rarity simulation can bundle this
// module outside of Nuxt.
import { ascensionEliteGauntlet, ascensionPressureMult } from '~/data/ascensions'
import { rollEliteModifiers } from '~/data/eliteModifiers'
import { applyBossSkill, BOSS_SKILL_IDS, type BossSkillId } from '~/data/bossSkills'
import { generateMission } from '~/utils/missiongen'
import { rand, randInt, sample, weightedPick } from '~/utils/random'
import {
  ELEMENTS,
  type EncounterPreview,
  type EncounterTier,
  type GameMap,
  type MapNode,
  type NodeType,
} from '~/types/game'

// Focus sets must cover what missions can actually require (medium
// missions use up to 2 elements), so previews never understate threats.
const FOCUS_ELEMENT_COUNT: Record<string, number> = {
  swarm: 2,
  mixed: 2,
  titan: 2,
  elite: 2,
  boss: 3,
  // Puzzle threats mirror the player's own roster — no posted focus.
  puzzle: 0,
}

/**
 * 🌊 Element currents (v2.38, per Javi: drafting a team and then hitting
 * off-element rows 5+ "can completely tank your run"): each act grows
 * TWO winding same-pair "currents" — random walks from the act's first
 * row to the row below the boss. Combat nodes on a current adopt its
 * element pair 65% of the time (never 100% — just more likely), so the
 * map shows coherent, followable element routes that wander sideways.
 * Rolled at generation, BEFORE the draft: scout the currents, then
 * draft to them. Bosses and puzzles are never touched.
 */
function carveElementCurrents(segment: MapNode[][]) {
  const four = sample([...ELEMENTS], 4)
  const pairs: [typeof ELEMENTS[number], typeof ELEMENTS[number]][] = [
    [four[0]!, four[1]!],
    [four[2]!, four[3]!],
  ]
  for (const pair of pairs) {
    let col = randInt(0, segment[0]!.length - 1)
    for (let local = 0; local < segment.length - 1; local++) {
      const row = segment[local]!
      col = Math.max(0, Math.min(row.length - 1, col + randInt(-1, 1)))
      // The lane node adopts the pair 80% of the time; a side neighbor
      // 30% — chest/rest rows and mystery/shop conversions eat enough
      // stops that anything softer reads as noise (measured, not vibes).
      const paint = (node: MapNode | undefined, chance: number) => {
        if (
          node && node.type !== 'boss' && node.type !== 'puzzle'
          && node.preview?.focusElements?.length === 2
          && rand() < chance
        ) {
          node.preview.focusElements = sample([...pair], 2)
        }
      }
      paint(row[col], 0.8)
      paint(row[col + (rand() < 0.5 ? 1 : -1)], 0.3)
    }
  }
}

/** 🎏 v4.83 (per Javi: "my buddy had a game where all lanes were water
 * fire" — the act's OPENING row must offer real choice): no two nodes
 * on an act's first row may post the same element pair. Duplicates
 * re-roll toward the row's least-seen elements so the start fans out;
 * the first holder of a pair keeps it, so a current's route survives. */
export function diversifyOpeningRow(segment: MapNode[][]) {
  const row = segment[0] ?? []
  const keyOf = (els: readonly string[]) => [...els].sort().join('+')
  const seen = new Set<string>()
  const used: Record<string, number> = {}
  for (const node of row) {
    const focus = node.preview?.focusElements
    if (focus?.length !== 2) continue
    if (!seen.has(keyOf(focus))) {
      seen.add(keyOf(focus))
      for (const e of focus) used[e] = (used[e] ?? 0) + 1
      continue
    }
    const candidates: [typeof ELEMENTS[number], typeof ELEMENTS[number]][] = []
    for (let i = 0; i < ELEMENTS.length; i++) {
      for (let j = i + 1; j < ELEMENTS.length; j++) {
        if (!seen.has(keyOf([ELEMENTS[i]!, ELEMENTS[j]!]))) candidates.push([ELEMENTS[i]!, ELEMENTS[j]!])
      }
    }
    if (candidates.length === 0) continue // >10 lanes can't all differ
    const scored = candidates
      .map(pair => ({ pair, score: (used[pair[0]] ?? 0) + (used[pair[1]] ?? 0) + rand() * 0.5 }))
      .sort((a, b) => a.score - b.score)
    const pick = scored[0]!.pair
    node.preview!.focusElements = sample([...pick], 2)
    seen.add(keyOf(pick))
    for (const e of pick) used[e] = (used[e] ?? 0) + 1
  }
}

/** 🧩 Trials never stack (v2.38, per Javi): a puzzle row clears every
 * puzzle from the row directly above it. */
function spacePuzzles(segment: MapNode[][], act: number, pressureMult: number) {
  for (let local = 1; local < segment.length; local++) {
    if (!segment[local - 1]!.some(n => n.type === 'puzzle')) continue
    for (const node of segment[local]!) {
      if (node.type === 'puzzle') {
        node.type = 'swarm'
        node.preview = makePreview('swarm', local, act, undefined, pressureMult)
      }
    }
  }
}

export function tierForRow(type: NodeType, row: number): EncounterTier {
  if (type === 'elite') return 'elite'
  if (type === 'boss') return 'boss'
  // 🗿 v5.42: the titan previews as HARD whatever its row — its
  // missions roll medium/hard from byNodeType and wear titanReqMult.
  if (type === 'titan') return 'hard'
  if (row <= TUNING.map.easyMaxRow) return 'easy'
  if (row <= TUNING.map.mediumMaxRow) return 'medium'
  return 'hard'
}

function rollType(row: number, chestRow: number): NodeType {
  // First row eases the player in with a plain combat node.
  if (row === 0) return 'swarm'
  // Path balance: one all-chest row, and an all-rest row before the boss.
  if (row === chestRow) return 'chest'
  if (row === TUNING.map.rows - 2) return 'rest'
  return weightedPick(Object.entries(TUNING.map.typeWeights) as [NodeType, number][])
}

/** Later acts demand more successes per node (goalBonus); boss stays 1. */
export function goalBonusForAct(act: number, type: NodeType): number {
  if (type === 'boss') return 0
  const scale = TUNING.acts.scaling[Math.min(Math.max(act, 1), TUNING.acts.scaling.length) - 1]!
  return scale.goalBonus
}

function makePreview(type: NodeType, row: number, act: number, bossSkill?: BossSkillId, pressureMult = 1): EncounterPreview | null {
  if (type === 'rest' || type === 'chest' || type === 'mystery' || type === 'shop') return null
  const [min, max] = TUNING.map.requiredSuccesses[type as keyof typeof TUNING.map.requiredSuccesses]
  const preview: EncounterPreview = {
    requiredSuccesses: randInt(min, max) + goalBonusForAct(act, type),
    focusElements: sample(ELEMENTS, FOCUS_ELEMENT_COUNT[type] ?? 2),
    tier: tierForRow(type, row),
  }
  if (type === 'boss') {
    // The boss is rolled ONCE, at map generation: the player can preview
    // its exact statline AND its skill, and every (re)spawn is the same.
    // Ascension pressure (A2/A8) hardens the roll here, pre-preview.
    const probe: MapNode = { id: 'boss-probe', row, col: 0, type, next: [], preview }
    const rolled = generateMission(probe, act, 1, { pressureMult })
    preview.boss = {
      name: rolled.name,
      required: rolled.required,
      damage: rolled.damage,
      xp: rolled.xp,
      slots: rolled.slots,
    }
    applyBossSkill(preview.boss, bossSkill ?? BOSS_SKILL_IDS[Math.floor(rand() * BOSS_SKILL_IDS.length)]!)
  }
  return preview
}

/**
 * Connects two consecutive rows with LOCAL edges only: every node links
 * to its nearest neighbor in the next row, plus occasional adjacent
 * branches — a left-hand node never points across the whole map. Every
 * node keeps at least one edge on each side, so every node stays on at
 * least one full path (the elite/shop path guarantees rely on this).
 */
function connectRows(current: MapNode[], next: MapNode[]) {
  // 🧶 v5.44 (per Javi's map screenshot: "still needs to be cleaned
  // up"): PLANAR LADDER, crossing-free BY CONSTRUCTION. The old
  // nearest+variety+uncross trio still wove X patterns; this walks two
  // column-sorted pointers and only ever steps i, j, or both — every
  // edge sequence is monotone, so no two roads can cross. Every node on
  // both sides keeps at least one edge (the pointers must reach both
  // ends), which is the path guarantee elites/shops rely on.
  // 🗿 v5.45: titans live OUTSIDE the lattice — appended edge nodes,
  // wired by their own pass. The ladder never sees them.
  const cur = [...current].filter(n => n.type !== 'titan').sort((a, b) => a.col - b.col)
  const nxt = [...next].filter(n => n.type !== 'titan').sort((a, b) => a.col - b.col)
  const link = (i: number, j: number) => {
    if (!cur[i]!.next.includes(nxt[j]!.id)) cur[i]!.next.push(nxt[j]!.id)
  }
  // Rows that lead into an all-same non-combat row (the chest row, the
  // pre-boss rest row) get MINIMAL wiring — one clean road each, since
  // branching between identical destinations is pure visual noise.
  const minimal = nxt.length > 0
    && nxt.every(n => n.type === nxt[0]!.type)
    && (nxt[0]!.type === 'chest' || nxt[0]!.type === 'rest')
  const progress = (k: number, length: number) => (length <= 1 ? 0.5 : k / (length - 1))
  let i = 0
  let j = 0
  link(i, j)
  while (i < cur.length - 1 || j < nxt.length - 1) {
    const canI = i < cur.length - 1
    const canJ = j < nxt.length - 1
    let move: 'i' | 'j' | 'both'
    if (!canI) move = 'j'
    else if (!canJ) move = 'i'
    else if (minimal) move = 'both'
    else if (rand() < 0.3) move = 'both'
    else move = progress(i, cur.length) <= progress(j, nxt.length) ? 'i' : 'j'
    if (move !== 'j') i++
    if (move !== 'i') j++
    link(i, j)
  }
}

/**
 * One continuous map spanning all acts. Each act is a segment following
 * the path-balance rules (intro combat row, one all-chest row, all-rest
 * row before the boss, scattered mysteries), and each act boss connects
 * directly to the next act's starting nodes.
 */
export function generateMap(ascension = 0, weeklyMod: string | null = null): GameMap {
  const { rows: rowsPerAct, minNodesPerRow, maxNodesPerRow, chestRowRange, mysteryCount } = TUNING.map
  const acts = TUNING.acts.count
  // Ascension riders (v0.80/v0.83): A2/A8 harden boss statlines; A6
  // turns one row per act into an elite gauntlet.
  const pressureMult = ascensionPressureMult(ascension)
  const eliteGauntlet = ascensionEliteGauntlet(ascension)
  const rowNodes: MapNode[][] = []
  // Boss skills are UNIQUE within a run (v0.48): draw distinct skills
  // from the pool, one per act boss.
  const bossSkills = sample([...BOSS_SKILL_IDS], acts)

  for (let act = 0; act < acts; act++) {
    const chestRow = randInt(chestRowRange[0], chestRowRange[1])
    const segment: MapNode[][] = []

    for (let local = 0; local < rowsPerAct; local++) {
      const row = act * rowsPerAct + local
      const isBoss = local === rowsPerAct - 1
      const count = isBoss ? 1 : randInt(minNodesPerRow, maxNodesPerRow)
      const nodes: MapNode[] = []
      for (let col = 0; col < count; col++) {
        const type = isBoss ? 'boss' : rollType(local, chestRow)
        nodes.push({
          id: `n${row}-${col}`,
          row,
          col,
          type,
          next: [],
          preview: makePreview(type, local, act + 1, isBoss ? bossSkills[act] : undefined, pressureMult),
        })
      }
      segment.push(nodes)
    }

    // Elite placement. Normally exactly elitesPerAct NODES per act on
    // mid combat rows whose siblings stay elite-free, so every act is
    // guaranteed both an elite path and an elite-free path (the
    // staircase edges put every node on at least one full path).
    // A6+ (v0.83, per Javi): one whole row becomes an ELITE GAUNTLET —
    // every path through the act crosses an elite; no dodging.
    const eliteEligible = segment.flat().filter((n) => {
      const local = n.row - act * rowsPerAct
      return local >= TUNING.map.eliteMinRow && local < rowsPerAct - 2 && local !== chestRow
    })
    // ⚔️ v5.42: every elite carries ONE modifier, rolled at generation
    // so the map can preview it. v5.43 (per Javi): the act's modifiers
    // draw WITHOUT replacement — no repeats within an act.
    const eliteNodes = eliteGauntlet
      ? (() => {
          const rows = [...new Set(eliteEligible.map(n => n.row))]
          const gauntletRow = sample(rows, 1)[0]
          return segment.flat().filter(n => n.row === gauntletRow)
        })()
      : sample(eliteEligible, Math.min(TUNING.map.elitesPerAct, eliteEligible.length))
    // 👿 v5.66 Twin Curses (weekly modifier): every elite draws TWO from
    // the same without-replacement batch — the pair on one node always
    // differs, and repeats only appear once the 8-deck runs dry.
    const twins = weeklyMod === 'twincurses'
    const modifiers = rollEliteModifiers(ascension, eliteNodes.length * (twins ? 2 : 1))
    eliteNodes.forEach((node, i) => {
      node.type = 'elite'
      node.preview = makePreview('elite', node.row - act * rowsPerAct, act + 1)
      node.modifier = modifiers[twins ? i * 2 : i]
      if (twins) node.modifier2 = modifiers[i * 2 + 1]
    })

    // 🗿 Titan placement (v5.45, per Javi: "make it an EXTRA on a row"):
    // the titan no longer replaces a node — it's APPENDED to the edge of
    // an eligible row (rows can reach 5 wide). The main lattice stays
    // untouched: the titan hangs off the margin as a true side-detour,
    // wired by the post-pass (edge inbound + a skip landing two rows
    // up). Constraints: at least three rows shy of the boss, never
    // skipping the CHEST row (the shortcut may cost a shop or mystery —
    // that's the trade — but the act's relic chest is sacred).
    const titanRowsEligible = [...new Set(segment.flat().map(n => n.row))].filter((row) => {
      const local = row - act * rowsPerAct
      return local >= TUNING.map.eliteMinRow && local < rowsPerAct - 3
        && local !== chestRow && local + 1 !== chestRow
    })
    for (const row of sample(titanRowsEligible, Math.min(TUNING.map.titansPerAct, titanRowsEligible.length))) {
      const local = row - act * rowsPerAct
      // 🗿 v5.46 (per Javi: 5-wide felt cramped): the titan floats in
      // the MARGIN between rows — col is a pure side marker (−1 left,
      // 99 right), never a lattice lane, so row spacing is untouched.
      segment[local]!.push({
        id: `n${row}-titan`,
        row,
        col: rand() < 0.5 ? -1 : 99,
        type: 'titan',
        next: [],
        preview: makePreview('titan', local, act + 1),
      })
    }

    // One shop per act, elite-style: siblings stay shop-free, so a shop
    // path and a shop-free path both always exist.
    const shopEligible = segment.flat().filter((n) => {
      const local = n.row - act * rowsPerAct
      return local > 0 && local < rowsPerAct - 2 && local !== chestRow && n.type !== 'elite' && n.type !== 'titan'
    })
    for (const node of sample(shopEligible, Math.min(TUNING.map.shopsPerAct, shopEligible.length))) {
      node.type = 'shop'
      node.preview = null
    }

    // A mid-act rest stop on one of the paths, shop-style: always in
    // front of the chest (relic) row and never in the first two rows.
    const restEligible = segment.flat().filter((n) => {
      const local = n.row - act * rowsPerAct
      return local >= 2 && local < chestRow && n.type !== 'elite' && n.type !== 'shop' && n.type !== 'titan'
    })
    for (const node of sample(restEligible, Math.min(TUNING.map.restStopsPerAct, restEligible.length))) {
      node.type = 'rest'
      node.preview = null
    }

    // Scatter mysteries (3–5 guaranteed per act) over combat rows.
    const eligible = segment
      .flat()
      .filter((n) => {
        const local = n.row - act * rowsPerAct
        return local > 0 && local < rowsPerAct - 2 && local !== chestRow
          && n.type !== 'elite' && n.type !== 'shop' && n.type !== 'rest' && n.type !== 'titan'
      })
    for (const node of sample(eligible, Math.min(randInt(mysteryCount[0], mysteryCount[1]), eligible.length))) {
      node.type = 'mystery'
      node.preview = null
    }

    spacePuzzles(segment, act + 1, pressureMult)
    carveElementCurrents(segment)
    diversifyOpeningRow(segment)

    // The previous act's boss opens into this act's starting nodes.
    if (act > 0) {
      const prevBoss = rowNodes[rowNodes.length - 1]![0]!
      prevBoss.next.push(...segment[0]!.map(n => n.id))
    }
    rowNodes.push(...segment)
  }

  for (let i = 0; i < rowNodes.length; i++) {
    // Boss rows already point at the next act's start.
    if ((i + 1) % rowsPerAct === 0) continue
    connectRows(rowNodes[i]!, rowNodes[i + 1]!)
  }

  // 🗿 v5.45: wire each titan as a SIDE-DETOUR — inbound from the
  // previous row's same-side edge node, outbound skipping straight to
  // the same-side edge node two rows up. The lattice never knew the
  // titan existed, so nothing needs orphan repair and the whole detour
  // hugs the margin.
  for (let i = 0; i < rowNodes.length; i++) {
    for (const titan of rowNodes[i]!.filter(n => n.type === 'titan')) {
      const onLeft = titan.col < 0
      const edgeOf = (row: MapNode[]) => {
        const pool = row.filter(n => n.type !== 'titan')
        return pool.reduce((a, b) => ((onLeft ? b.col < a.col : b.col > a.col) ? b : a))
      }
      const donor = edgeOf(rowNodes[i - 1]!)
      if (!donor.next.includes(titan.id)) donor.next.push(titan.id)
      titan.next = [edgeOf(rowNodes[i + 2]!).id]
    }
  }

  return { rows: rowsPerAct * acts, rowsPerAct, nodes: rowNodes.flat() }
}

/**
 * ⚔️ Gauntlet act (v2.34, per Javi): builds act N (N ≥ 4) one act past
 * the current summit and stitches it onto the live map — same path
 * balance rules as generateMap's act segments. Boss skills CYCLE the
 * full roster: anything used in the last (roster−1) act bosses is
 * excluded, so all 12 bosses appear before any repeats.
 */
export function appendGauntletAct(map: GameMap, act: number, ascension: number): void {
  const { rows: rowsPerAct, minNodesPerRow, maxNodesPerRow, chestRowRange, mysteryCount } = TUNING.map
  const pressureMult = ascensionPressureMult(ascension)
  const eliteGauntlet = ascensionEliteGauntlet(ascension)

  const usedInOrder = map.nodes
    .filter(n => n.type === 'boss' && n.preview?.boss?.skill)
    .sort((a, b) => a.row - b.row)
    .map(n => n.preview!.boss!.skill as BossSkillId)
  const recent = new Set(usedInOrder.slice(-(BOSS_SKILL_IDS.length - 1)))
  const fresh = BOSS_SKILL_IDS.filter(id => !recent.has(id))
  const pool = fresh.length > 0 ? fresh : [...BOSS_SKILL_IDS]
  const bossSkill = pool[Math.floor(rand() * pool.length)]!

  const rowStart = map.rows
  const chestRow = randInt(chestRowRange[0], chestRowRange[1])
  const segment: MapNode[][] = []
  for (let local = 0; local < rowsPerAct; local++) {
    const row = rowStart + local
    const isBoss = local === rowsPerAct - 1
    const count = isBoss ? 1 : randInt(minNodesPerRow, maxNodesPerRow)
    const nodes: MapNode[] = []
    for (let col = 0; col < count; col++) {
      const type = isBoss ? 'boss' : rollType(local, chestRow)
      nodes.push({
        id: `n${row}-${col}`,
        row,
        col,
        type,
        preview: makePreview(type, local, act, isBoss ? bossSkill : undefined, pressureMult),
        next: [],
      })
    }
    segment.push(nodes)
  }

  // Elite / shop / rest / mystery placement — the same balance rules as
  // generateMap, with rows local to this segment.
  const localOf = (n: MapNode) => n.row - rowStart
  const eliteEligible = segment.flat().filter(n =>
    localOf(n) >= TUNING.map.eliteMinRow && localOf(n) < rowsPerAct - 2 && localOf(n) !== chestRow)
  // v5.43: draws without replacement — no modifier repeats in the act.
  const gauntletEliteNodes = eliteGauntlet
    ? (() => {
        const rows = [...new Set(eliteEligible.map(n => n.row))]
        const gauntletRow = sample(rows, 1)[0]
        return segment.flat().filter(n => n.row === gauntletRow)
      })()
    : sample(eliteEligible, Math.min(TUNING.map.elitesPerAct, eliteEligible.length))
  const gauntletModifiers = rollEliteModifiers(ascension, gauntletEliteNodes.length)
  gauntletEliteNodes.forEach((node, i) => {
    node.type = 'elite'
    node.preview = makePreview('elite', localOf(node), act)
    node.modifier = gauntletModifiers[i]
  })
  // (No titans in gauntlet acts — their row-skip would fight the seam
  // stitching; the gauntlet's pressure curve is its own reward.)
  const shopEligible = segment.flat().filter(n =>
    localOf(n) > 0 && localOf(n) < rowsPerAct - 2 && localOf(n) !== chestRow && n.type !== 'elite')
  for (const node of sample(shopEligible, Math.min(TUNING.map.shopsPerAct, shopEligible.length))) {
    node.type = 'shop'
    node.preview = null
  }
  const restEligible = segment.flat().filter(n =>
    localOf(n) >= 2 && localOf(n) < chestRow && n.type !== 'elite' && n.type !== 'shop')
  for (const node of sample(restEligible, Math.min(TUNING.map.restStopsPerAct, restEligible.length))) {
    node.type = 'rest'
    node.preview = null
  }
  const mysteryEligible = segment.flat().filter(n =>
    localOf(n) > 0 && localOf(n) < rowsPerAct - 2 && localOf(n) !== chestRow
    && n.type !== 'elite' && n.type !== 'shop' && n.type !== 'rest')
  for (const node of sample(mysteryEligible, Math.min(randInt(mysteryCount[0], mysteryCount[1]), mysteryEligible.length))) {
    node.type = 'mystery'
    node.preview = null
  }

  spacePuzzles(segment, act, pressureMult)
  carveElementCurrents(segment)
  diversifyOpeningRow(segment)

  // The fallen boss (last row of the old map) opens into the new act.
  for (const boss of map.nodes.filter(n => n.row === rowStart - 1)) {
    boss.next.push(...segment[0]!.map(n => n.id))
  }
  for (let i = 0; i < segment.length - 1; i++) connectRows(segment[i]!, segment[i + 1]!)

  map.nodes.push(...segment.flat())
  map.rows = rowStart + rowsPerAct
}

/**
 * 🤫 A10 secret boss (v0.84): built only when the act-3 boss falls on
 * Ascension 10 — a hidden FOURTH boss one row past the old summit. It
 * wields a skill the run hasn't seen and an act-3 statline hardened
 * ~15% past the ascension pressure. The caller links it and bumps
 * map.rows so the board grows to show it.
 */
export function makeSecretBossNode(map: GameMap, ascension: number): MapNode {
  const usedSkills = new Set(
    map.nodes.map(n => n.preview?.boss?.skill).filter((s): s is BossSkillId => !!s),
  )
  const fresh = BOSS_SKILL_IDS.filter(id => !usedSkills.has(id))
  const skill = fresh.length > 0
    ? fresh[Math.floor(rand() * fresh.length)]!
    : BOSS_SKILL_IDS[Math.floor(rand() * BOSS_SKILL_IDS.length)]!
  const row = map.rows
  return {
    id: `n${row}-secret`,
    row,
    col: 0,
    type: 'boss',
    next: [],
    preview: makePreview(
      'boss',
      TUNING.map.rows - 1,
      TUNING.acts.count,
      skill,
      ascensionPressureMult(ascension) * 1.15,
    ),
  }
}
